/**
 * OIDC redirect landing page. ZITADEL bounces back here with `code` +
 * `state` query params; we exchange them for tokens, then navigate home.
 *
 * Lives outside `<AppLayout>` (sibling top-level route in App.tsx) so the
 * AuthGate doesn't redirect us away from the callback URL before the token
 * exchange completes. Chrome here is intentionally minimal — the user is
 * only here for the duration of one fetch round-trip.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signinCallback } from "@/features/auth/user-manager";
import { warmUpBff } from "@/features/auth/warm-up";

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The client picker's first /api call lands right after this; wake the
    // BFF in parallel with the token exchange (fresh page load, so the
    // login page's ping doesn't carry over).
    warmUpBff();
    void signinCallback()
      .then(() => {
        if (cancelled) return;
        navigate("/", { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="bg-background text-foreground flex h-full flex-col items-center justify-center gap-4">
      <div
        aria-hidden
        className="animate-pulse rounded-full"
        style={{
          width: 10,
          height: 10,
          background: "var(--accent-sky)",
          boxShadow: "var(--glow-sky)",
        }}
      />
      <p
        className="font-mono uppercase"
        style={{
          color: error ? "var(--accent-red)" : "rgba(var(--accent-sky-rgb), 0.85)",
          fontSize: "var(--fs-label)",
          letterSpacing: "0.28em",
        }}
      >
        {error ? "Sign-in failed" : "Completing sign in…"}
      </p>
      {error && (
        <p
          className="font-mono"
          style={{
            color: "var(--text-secondary)",
            fontSize: "var(--fs-micro)",
            maxWidth: 480,
            textAlign: "center",
          }}
        >
          {error}
        </p>
      )}
      {error && (
        <button
          type="button"
          onClick={() => navigate("/", { replace: true })}
          className="font-mono uppercase"
          style={{
            padding: "6px 14px",
            background: "rgba(var(--accent-true-blue-rgb), 0.10)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
            borderRadius: 6,
            color: "var(--accent-sky)",
            fontSize: "var(--fs-label)",
            letterSpacing: "0.32em",
            cursor: "pointer",
          }}
        >
          Back to sign in
        </button>
      )}
    </div>
  );
}
